import { fetchCustomers, SET_SEARCH_RESULTS } from "@rpforms/shared";
import { search } from "@rpforms/shared/build/actions/search";
import { Content } from "@rpforms/shared/build/components/layout/Content";
import PageNavbar from "@rpforms/shared/build/components/universal/PageNavbar";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import CustomerListRow from "../components/customers/CustomerListRow";
import EntityListRow from "../components/entities/EntityListRow";
import EntryRow from "../components/entries/EntryRow";
import {
    GenerateDocumentDropdown,
    validateDocumentSelection,
} from "../components/entries/GenerateDocumentDropdown";
import InventoryItemTableRow from "../components/inventory_items/InventoryItemTableRow";
import { GenerateTarget, useGenerateOffer } from "../hooks/useGenerateOffer";
import { RootState } from "../reducers";

export const SearchPage = () => {
    const { query } = useParams();
    const dispatch = useDispatch();
    const searchResults = useSelector<RootState, any>((state) => state.search.results);
    const formEntries = useSelector<RootState, any>((state) => state.formEntries);
    const customers = useSelector<RootState, any>((state) => state.customers);
    const [selectedEntryIds, setSelectedEntryIds] = useState<number[]>([]);
    const { generate, isGenerating, error, setError, notesModal } = useGenerateOffer();

    const MIN_CHARS = 2;

    useEffect(() => {
        if (!customers.isLoading && customers.customers.length <= 0) {
            dispatch(fetchCustomers(1, () => {}));
        }
    }, []);

    useEffect(() => {
        if (error) {
            toast.error(error);
            setError(null);
        }
    }, [error]);

    // Query Backend
    useEffect(() => {
        setSelectedEntryIds([]);
        if (validQuery()) {
            dispatch(search(query));
        } else {
            const ts = +new Date();
            dispatch({
                type: SET_SEARCH_RESULTS,
                payload: {
                    customers: [],
                    entities: [],
                    form_entries: [],
                    inventory_items: [],
                    timestamp: ts,
                },
            });
        }
    }, [query]);

    const decodedQuery = decodeURIComponent(query);

    const validQuery = () => {
        return query && query.length >= MIN_CHARS;
    };

    const description = () => {
        return validQuery() ? (
            <>
                Gefunden: {searchResults.customers.length} Kunden, {searchResults.entities.length}{" "}
                Objekte, {searchResults.form_entries.length} Protokolle
            </>
        ) : (
            <>bitte mindestens {MIN_CHARS} Zeichen eingeben</>
        );
    };

    const title = () => {
        return validQuery() ? `Suche nach: ${decodedQuery}` : <>Suche</>;
    };

    const entries = () => {
        if (!searchResults.form_entries) {
            return [];
        }
        return (searchResults.form_entries = searchResults.form_entries.map((entry) => {
            const storedFormEntry = formEntries.byId[`entry_${entry.id}`];
            if (storedFormEntry) {
                return storedFormEntry;
            }
            return entry;
        }));
    };

    const selectedEntries = () =>
        selectedEntryIds.map((id) => entries().find((entry) => entry.id === id)).filter(Boolean);

    const handleSelectEntry = (entryId: number, isSelected: boolean) => {
        setSelectedEntryIds((prev) =>
            isSelected ? [...prev, entryId] : prev.filter((id) => id !== entryId)
        );
    };

    const handleGenerateDocument = async (target: GenerateTarget) => {
        const selected = selectedEntries();
        const validationError = validateDocumentSelection(target, selected);
        if (validationError) {
            toast.error(validationError);
            return;
        }
        await generate(
            target,
            selected.map((entry) => entry.id),
            { cache: new Map(selected.map((entry) => [entry.id, { formEntry: entry }])) }
        );
    };

    return (
        <>
            {notesModal}
            <PageNavbar title={title()} description={description()}>
                {selectedEntryIds.length > 0 && (
                    <GenerateDocumentDropdown
                        selectedEntries={selectedEntries()}
                        isGenerating={isGenerating}
                        onGenerate={handleGenerateDocument}
                    />
                )}
            </PageNavbar>
            {validQuery() && (
                <Content>
                    <h4>Kunden</h4>
                    {searchResults.customers.map((customer: ICustomer) => {
                        return <CustomerListRow key={customer.id} customer={customer} />;
                    })}
                    <hr />

                    <h4>Objekte</h4>
                    {searchResults.entities.map((entity: IEntity) => {
                        return (
                            <EntityListRow key={entity.id} entity={entity} deleteEntity={null} />
                        );
                    })}
                    <hr />
                    <h4>Protokolle</h4>
                    {entries().map((entry: any) => {
                        return (
                            <EntryRow
                                key={entry.id}
                                entry={entry}
                                deleteFormEntry={null}
                                isSelected={selectedEntryIds.includes(entry.id)}
                                onSelect={(isSelected) => handleSelectEntry(entry.id, isSelected)}
                            />
                        );
                    })}
                    <hr />
                    <h4>Leistungsverzeichnis</h4>
                    {searchResults.inventory_items.map((inventoryItem: any) => {
                        return (
                            <InventoryItemTableRow
                                key={inventoryItem.id}
                                inventoryItem={inventoryItem}
                                presentationType={"list-row"}
                            />
                        );
                    })}
                </Content>
            )}
        </>
    );
};

export default SearchPage;
